import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const Analytics = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/analytics/events`);
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setRows([]);
        setError(err.response?.data?.error || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totals = rows.reduce((acc, r) => ({
    batches: acc.batches + (r.batch_count ?? 0),
    photos: acc.photos + (r.photo_count ?? 0),
    visits: acc.visits + (r.page_visits ?? 0),
    verified: acc.verified + (r.face_verified_count ?? r.verification_count ?? 0),
    imgDownloads: acc.imgDownloads + (r.download_image_count ?? 0),
    zipDownloads: acc.zipDownloads + (r.download_zip_count ?? 0),
    allPhotoViews: acc.allPhotoViews + (r.view_all_photo_count ?? 0),
  }), { batches: 0, photos: 0, visits: 0, verified: 0, imgDownloads: 0, zipDownloads: 0, allPhotoViews: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Event Analytics</h1>
        <p className="text-sm text-slate-500 mt-0.5">Per-event: page visits, face verifications, downloads, All Photos views</p>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 p-4 bg-rose-50 border border-rose-200 rounded-2xl">
          <svg className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-rose-700 text-sm">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <p className="text-slate-500 text-sm">No events or analytics yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide sticky left-0 bg-white">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">User</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">Batches</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">Photos</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">Page visits</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-violet-400 uppercase tracking-wide">Face verified</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">Img downloads</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">ZIP downloads</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">All photo views</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-800 sticky left-0 bg-white max-w-[160px] truncate">{r.event_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 max-w-[140px] truncate">{r.user_name}{r.user_phone ? ` (${r.user_phone})` : ''}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-600">{r.batch_count ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-600">{r.photo_count ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-600">{r.page_visits ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-violet-700 bg-violet-50/50">{r.face_verified_count ?? r.verification_count ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-600">{r.download_image_count ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-600">{r.download_zip_count ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-600">{r.view_all_photo_count ?? 0}</td>
                  </tr>
                ))}
                {rows.length > 1 && (
                  <tr className="bg-slate-50 border-t border-slate-200">
                    <td className="px-4 py-2.5 text-xs font-bold text-slate-500 uppercase sticky left-0 bg-slate-50">Totals</td>
                    <td className="px-4 py-2.5" />
                    <td className="px-4 py-2.5 text-sm font-bold text-right text-slate-700">{totals.batches}</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-right text-slate-700">{totals.photos}</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-right text-slate-700">{totals.visits}</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-right text-violet-700 bg-violet-50/50">{totals.verified}</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-right text-slate-700">{totals.imgDownloads}</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-right text-slate-700">{totals.zipDownloads}</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-right text-slate-700">{totals.allPhotoViews}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
